import { Fragment, useState } from 'react';
import { ChevronDown, ChevronRight, Eye, EyeOff, Home, PlaneTakeoff, Replace, Scale, Ticket, Users } from 'lucide-react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { revealField } from '@/lib/passengers/api';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/cn';
import { formatFlightTime } from '@/lib/disruptions/airport-tz';
import { stampTitle } from '@/lib/disruptions/operator-stamp';
import type {
  DisruptionPaxRosterRow,
  DisruptionPnrGroupRow,
  RebookingGroupResult,
} from '@/lib/disruptions/types';
import { goingHomeOf } from '@/lib/disruptions/going-home';
import { SuggestedFlightCell } from '@/routes/disruptions/suggested-flight-cell';
import { SeatHoldChip } from '@/components/pnr/seat-hold-chip';
import {
  formatPaxSummary,
  noOptionsReasonLabel,
  priorityShortLabel,
  rebookingStatusLabel,
  REBOOKING_STATUS_TONE,
} from '@/routes/disruptions/labels';
import { PnrNotificationCell } from './pnr-notification-cell';
import { PnrSourceMark } from '@/components/provenance/pnr-source-mark';

// PNR-grouped roster row + its presentational leaves — shared between the
// disruption control-center roster (routes/disruptions/roster-tab) and the
// case-detail Passengers tab so both render the SAME expandable PNR row (members,
// audited name reveal, hotel/voucher/triage badges) and open the operation center
// on click. Friendly labels primary, URNs secondary (frontend-conventions §18).

/** The `t` the plain helpers below are handed — the same bound function the
 *  components get from `useTranslation('pnr')`. */
type Translate = TFunction<'pnr'>;

interface PnrRowProps {
  group: DisruptionPnrGroupRow;
  onOpen: () => void;
  /**
   * Render the per-PNR notification + acceptance-gate status cell (N11). Opt-in
   * (default off) so the shared disruption-roster caller is unchanged and only
   * the case-detail Passengers tab — which mounts inside the QueryClientProvider
   * and joins the case WS room — pulls the live notification query.
   */
  showNotifications?: boolean;
  /**
   * Render the SUGGESTED FLIGHT column cell (disruption-roster-mvp-parity / U1).
   * Opt-in (default off) so the case-detail Passengers tab — whose table has no
   * such column — is unaffected; only the disruption control-center roster (which
   * adds the matching `<th>` and joins the rebooking-detail read) turns it on. The
   * joined `suggested` outcome (by `groupId === subCaseUrn`) drives the cell.
   */
  showSuggested?: boolean;
  /** The group's rebooking outcome, joined by the caller from `useRebookingDetail`
   *  (`groupId === group.subCaseUrn`). Undefined until it lands / when absent. */
  suggested?: RebookingGroupResult | undefined;
}

/**
 * One row per PNR. Clicking the row (or the PNR locator) opens the detail drawer
 * — there is NO expander chevron (it misaligned the grid, NEX-299 follow-up); the
 * member passengers live in the drawer. The pax chip shows only for a group
 * (>1 pax). When a flight is assigned, the "Flight assigned" badge is a button
 * that opens the assigned-flight dialog.
 */
export function PnrRow({
  group,
  onOpen,
  showNotifications = false,
  showSuggested = false,
  suggested,
}: PnrRowProps) {
  const { t } = useTranslation('pnr');
  const [expanded, setExpanded] = useState(false);
  const pax = group.passengers ?? [];
  const goingHome = goingHomeOf(group);
  // Only multi-pax PNRs expand — a single-pax group has nothing to reveal beyond
  // its own row, so the chevron would be noise (its detail lives in the drawer).
  const canExpand = pax.length > 1;
  const showRows = expanded && canExpand;
  return (
    <Fragment>
      {/* PNR group header. Per-element clickable (NO whole-row click): the chevron
          expands the passengers, the PNR label + "Details" open the detail. */}
      <tr className="border-b border-border transition-colors hover:bg-muted/40" data-testid="roster-pnr-row">
        <td className="px-3 py-2 align-top">
          <div className="flex items-start gap-1.5">
            {canExpand ? (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="mt-0.5 shrink-0 text-muted-foreground hover:text-foreground"
                aria-label={expanded ? t('roster.collapsePax') : t('roster.expandPax')}
                data-testid="roster-pnr-expand"
              >
                {expanded ? (
                  <ChevronDown className="h-4 w-4" aria-hidden />
                ) : (
                  <ChevronRight className="h-4 w-4" aria-hidden />
                )}
              </button>
            ) : (
              // Spacer keeps single-pax PNR labels aligned with expandable ones.
              <span className="mt-0.5 inline-block h-4 w-4 shrink-0" aria-hidden />
            )}
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-1.5">
                <button
                  type="button"
                  className="text-left font-medium text-primary underline-offset-2 hover:underline"
                  onClick={onOpen}
                  data-testid="roster-pnr-open"
                >
                  {group.pnrLabel}
                </button>
                <PnrSourceMark source={group.pnrSource} className="shrink-0" testId="roster-pnr-source" />
                {group.paxCount > 1 ? (
                  <Badge
                    variant="neutral"
                    size="sm"
                    title={formatPaxSummary(group.paxCount, group.paxBreakdown)}
                    data-testid="roster-pnr-pax"
                  >
                    {t('roster.paxCount', { n: group.paxCount })}
                  </Badge>
                ) : null}
              </div>
              {group.leadName ? (
                <div className="mt-0.5 text-xs text-muted-foreground">{group.leadName}</div>
              ) : null}
              {/* PAX-id (U1): an opaque, mask-by-default passenger identifier under
                  the lead name — mirrors the MVP `passengerReference`. NEVER a raw
                  PNR locator (that's the primary link above); this is the system
                  URN's opaque tail so the operator can cross-reference a passenger
                  without exposing PII. */}
              {(() => {
                const paxId = paxOpaqueId(group);
                return paxId ? (
                  <div
                    className="mt-0.5 font-mono text-[10px] leading-tight text-muted-foreground/80"
                    data-testid="roster-pnr-paxid"
                    title={t('roster.paxIdTitle')}
                  >
                    {paxId}
                  </div>
                ) : null;
              })()}
            </div>
          </div>
        </td>
        <td className="px-3 py-2 align-top">
          <span className="font-medium">{group.flightLabel}</span>
          <AssignedFlight assigned={group.assignedFlight} />
          {group.optionsCount > 0 ? (
            // QA fix 5 — the cases `optionsCount` denorm is stamped at generation
            // time; when the joined rebooking read says every option departed,
            // the row must not claim "5 options" as if they were bookable.
            suggested?.optionsExpired === true ? (
              <button
                type="button"
                onClick={onOpen}
                title={t('roster.optionsExpiredTitle')}
                className="mt-0.5 inline-flex items-center gap-1 text-xs italic text-muted-foreground hover:text-primary"
                data-testid="roster-options-expired"
              >
                <Replace className="h-3 w-3" aria-hidden />
                {t('roster.optionsExpired')}
              </button>
            ) : (
              <button
                type="button"
                onClick={onOpen}
                title={t('roster.optionsTitle', { n: group.optionsCount })}
                className="mt-0.5 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
                data-testid="roster-options-btn"
              >
                <Replace className="h-3 w-3" aria-hidden />
                {t('roster.optionsCount', { count: group.optionsCount })}
              </button>
            )
          ) : null}
        </td>
        {showSuggested ? (
          <td className="px-3 py-2 align-top" data-testid="roster-suggested-flight">
            <SuggestedFlightCell result={suggested} status={group.rebookingStatus} />
          </td>
        ) : null}
        <td className="px-3 py-2 align-top">
          {group.priorityTags.length > 0 || (group.allocationBlockers?.length ?? 0) > 0 || group.party || goingHome ? (
            <div className="flex flex-wrap gap-1">
              {/* #811: the passenger chose "prefiero ir a mi domicilio" in the app. */}
              {goingHome ? (
                <Badge
                  variant="info"
                  size="sm"
                  title={[t('home.chipTitle'), goingHome.address].filter(Boolean).join(' — ')}
                  data-testid="roster-home"
                >
                  <Home className="mr-1 h-3 w-3" aria-hidden />
                  {t('home.chip')} · {goingHome.wantsTransportHome ? t('home.withTransport') : t('home.withoutTransport')}
                </Badge>
              ) : null}
              {/* Allocation party (#806 slice 2): assigned WITH the linked PNR(s) — same hotel, same vehicle. */}
              {group.party ? (
                <Badge
                  variant="info"
                  size="sm"
                  title={t('party.title', { pnrs: group.party.pnrLabels.filter((p) => p !== group.pnrLabel).join(', ') })}
                  data-testid="roster-party"
                >
                  <Users className="mr-1 h-3 w-3" aria-hidden />
                  {group.party.role === 'MEMBER'
                    ? t('party.with', { pnr: group.party.rootPnrLabel })
                    : t('party.root', { n: group.party.subCaseUrns.length - 1 })}
                </Badge>
              ) : null}
              {/* Minors guard (#806): the party is held back until the desk fixes a condition. */}
              {group.allocationBlockers?.includes('CONDITION_UNKNOWN') ? (
                <Badge
                  variant="error"
                  size="sm"
                  title={t('blockers.CONDITION_UNKNOWN.title', { n: group.conditionUnknownPaxUrns?.length ?? 0 })}
                  data-testid="roster-blocker-condition"
                >
                  {t('blockers.CONDITION_UNKNOWN.label')}
                </Badge>
              ) : null}
              {group.priorityTags.map((tag) => (
                <Badge key={tag} variant="warning" size="sm">
                  {priorityShortLabel(tag, t)}
                </Badge>
              ))}
            </div>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </td>
        <td className="px-3 py-2 align-top">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge variant={REBOOKING_STATUS_TONE[group.rebookingStatus]} size="sm">
                {rebookingStatusLabel(group.rebookingStatus, t)}
              </Badge>
              <SeatHoldChip
                holdExpiresAt={group.holdExpiresAt}
                isTicketed={!!group.ticketNumber}
              />
              {group.ticketNumber ? (
                <Badge
                  variant="success"
                  size="sm"
                  title={t('roster.ticketedTitle', { ticket: group.ticketNumber })}
                >
                  <Ticket className="mr-1 h-3 w-3" aria-hidden />
                  {t('roster.ticketed')}
                </Badge>
              ) : null}
              <HotelBadge group={group} />
              <OutcomeChips group={group} />
              <button
                type="button"
                onClick={onOpen}
                className="ml-auto inline-flex items-center gap-1 pl-2 text-xs text-muted-foreground hover:text-foreground"
                data-testid="roster-pnr-details"
              >
                <span className="hidden sm:inline">{t('roster.details')}</span>
                <ChevronRight className="h-4 w-4 shrink-0" aria-hidden />
              </button>
            </div>
            <NoOptionsReason group={group} />
            {hasTriage(t, group) ? (
              <div className="flex flex-wrap items-center gap-1.5">
                <TriageChips group={group} />
              </div>
            ) : null}
            {/* Passenger offer notification + acceptance-gate status (N11): per-PNR
                delivery + acceptance state, resend/channel, and the supervisor
                force action. Lives under the rebooking status because the offer
                (hotel/ticket) is what the passenger accepts. Opt-in per row. */}
            {showNotifications ? (
              <div className="mt-1.5 border-t border-border/60 pt-1.5">
                <PnrNotificationCell
                  caseUrn={group.caseUrn}
                  subCaseUrn={group.subCaseUrn}
                  groupLabel={group.pnrLabel || group.leadName || t('group.thisPnr')}
                />
              </div>
            ) : null}
          </div>
        </td>
      </tr>

      {/* Expanded per-passenger rows (the case-grid pattern): inline reveal name,
          paxType/tier/senior, per-pax needs, onward flight, operational status. */}
      {showRows
        ? pax.map((p) => (
            <tr
              key={p.paxUrn}
              className="border-b border-border bg-muted/20 text-xs"
              data-testid="roster-pax-row"
            >
              <td className="px-3 py-1.5 pl-9 align-top">
                <RevealablePaxName pax={p} />
                <div className="mt-0.5 flex flex-wrap items-center gap-1">
                  <Badge variant="muted" size="sm">
                    {p.paxType}
                  </Badge>
                  {p.loyaltyTier ? (
                    <Badge variant="info" size="sm">
                      {p.loyaltyTier}
                    </Badge>
                  ) : null}
                  {p.isSenior ? (
                    <Badge variant="neutral" size="sm">
                      {t('pax.senior')}
                    </Badge>
                  ) : null}
                </div>
              </td>
              {/* Rebooking is per-PNR, so every passenger shares the GROUP's
                  assigned flight + rebooking status — show those (inherited), not
                  the per-pax `onwardFlightLabel` (unpopulated → "—") or `p.status`
                  (an OPERATIONAL enum: AWAITING/CHECKED_IN/… that read as
                  "rebooking awaiting" in this column). */}
              <td className="px-3 py-1.5 align-top">
                <AssignedFlight assigned={group.assignedFlight} />
              </td>
              {/* Suggested flight is a per-PNR (group) decision, so the nested
                  per-passenger rows leave this column blank to keep the grid
                  aligned when the column is on. */}
              {showSuggested ? <td className="px-3 py-1.5 align-top" aria-hidden /> : null}
              <td className="px-3 py-1.5 align-top">
                <NeedsCodeBadges codes={p.specialNeeds} />
              </td>
              <td className="px-3 py-1.5 align-top">
                <div className="flex flex-col items-start gap-1">
                  <Badge variant={REBOOKING_STATUS_TONE[group.rebookingStatus]} size="sm">
                    {rebookingStatusLabel(group.rebookingStatus, t)}
                  </Badge>
                  {/* Operational status kept as a secondary chip — hidden when it's
                      the default AWAITING ("no action yet") so it isn't noise. */}
                  {p.status && p.status !== 'AWAITING' && p.status !== 'AWAITING_OPERATOR' ? (
                    <span
                      className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground"
                      // #813: a check-in says who marked it and when (operator stamp or passenger reply).
                      title={
                        (p.status === 'CHECKED_IN'
                          ? stampTitle(
                              { by: p.checkedInBy, byName: p.checkedInByName, at: p.checkedInAt },
                              t,
                              { passenger: p.checkInSource === 'PASSENGER_REPLY' },
                            )
                          : undefined) ?? t('pax.operationalTitle', { status: p.status })
                      }
                      data-testid="roster-pax-status"
                    >
                      {p.status}
                    </span>
                  ) : null}
                  <PaxConditionChip pax={p} />
                </div>
              </td>
            </tr>
          ))
        : null}
    </Fragment>
  );
}

/** Inline name reveal in the roster (auth-tds §4, operator-ui-tds §14): the name
 *  stays masked until the operator clicks the eye, which calls the audited reveal
 *  endpoint and shows the plaintext in place — same pattern as the case manifest. */
function RevealablePaxName({ pax }: { pax: DisruptionPaxRosterRow }) {
  const { t } = useTranslation('pnr');
  const [name, setName] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const revealed = name !== null;
  const toggle = async (): Promise<void> => {
    if (revealed) {
      setName(null);
      return;
    }
    setPending(true);
    try {
      const res = await revealField(pax.caseUrn, pax.paxUrn, 'name');
      setName(res.value);
    } catch {
      /* reveal failed — leave masked */
    } finally {
      setPending(false);
    }
  };
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn(revealed ? 'font-medium text-foreground' : 'text-muted-foreground')}>
        {name ?? pax.fullName}
      </span>
      <button
        type="button"
        onClick={() => void toggle()}
        disabled={pending}
        className="shrink-0 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
        aria-label={revealed ? t('reveal.hide') : t('reveal.show')}
        title={revealed ? t('reveal.hide') : t('reveal.show')}
        data-testid="roster-pax-reveal"
      >
        {revealed ? <EyeOff className="h-3.5 w-3.5" aria-hidden /> : <Eye className="h-3.5 w-3.5" aria-hidden />}
      </button>
    </span>
  );
}

/** Per-passenger assistance-need badges from the SSR/need codes on the row. */
function NeedsCodeBadges({ codes }: { codes?: string[] | undefined }) {
  const { t } = useTranslation('pnr');
  if (!codes || codes.length === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {codes.map((c) => (
        <Badge key={c} variant="warning" size="sm" title={c}>
          {needLabel(t, c)}
        </Badge>
      ))}
    </div>
  );
}

/** Condition chip on the roster passenger row (#813): shown when a desk set the
 *  condition (so the mark is visible with who/when) or when it is UNKNOWN (the
 *  blocker). A manifest-decided ADULT is not noise worth a chip here. */
function PaxConditionChip({ pax }: { pax: DisruptionPaxRosterRow }) {
  const { t } = useTranslation('pnr');
  if (!pax.condition) return null;
  if (pax.conditionSource !== 'OPERATOR' && pax.condition !== 'UNKNOWN') return null;
  const title =
    pax.conditionSource === 'OPERATOR'
      ? stampTitle({ by: pax.conditionSetBy, byName: pax.conditionSetByName, at: pax.conditionSetAt }, t)
      : undefined;
  return (
    <Badge
      variant={pax.condition === 'UNKNOWN' ? 'error' : 'neutral'}
      size="sm"
      title={title ?? t(`condition.value.${pax.condition}`)}
      data-testid="roster-pax-condition"
    >
      {t(`condition.value.${pax.condition}`)}
    </Badge>
  );
}

/** Desk-declared outcomes on the PNR row (#813): accommodation / care / transport
 *  outcome, each with "marcado por X a las HH:MM". The consumed meal voucher keeps
 *  #808's chip in `HotelBadge`; its tooltip carries the same stamp copy. */
function OutcomeChips({ group }: { group: DisruptionPnrGroupRow }) {
  const { t } = useTranslation('pnr');
  const acc = group.accommodationOutcome;
  const care = group.careOutcome;
  const transport = group.transportOutcome;
  if (!acc && !care && !transport) return null;
  return (
    <>
      {acc ? (
        <Badge
          variant="secondary"
          size="sm"
          title={stampTitle({ by: acc.decidedByUrn, byName: acc.decidedByName, at: acc.decidedAt }, t) ?? t(`outcomeChip.accommodation.${acc.outcome}`, { defaultValue: acc.outcome })}
          data-testid="roster-outcome-accommodation"
        >
          {t(`outcomeChip.accommodation.${acc.outcome}`, { defaultValue: acc.outcome })}
        </Badge>
      ) : null}
      {care ? (
        <Badge
          variant="info"
          size="sm"
          title={stampTitle({ by: care.decidedByUrn, byName: care.decidedByName, at: care.decidedAt }, t) ?? t(`outcomeChip.care.${care.outcome}`, { defaultValue: care.outcome })}
          data-testid="roster-outcome-care"
        >
          {t(`outcomeChip.care.${care.outcome}`, { defaultValue: care.outcome })}
        </Badge>
      ) : null}
      {transport ? (
        <Badge
          variant="muted"
          size="sm"
          title={stampTitle({ by: transport.decidedByUrn, byName: transport.decidedByName, at: transport.decidedAt }, t) ?? t(`outcomeChip.transport.${transport.outcome}`, { defaultValue: transport.outcome })}
          data-testid="roster-outcome-transport"
        >
          {t(`outcomeChip.transport.${transport.outcome}`, { defaultValue: transport.outcome })}
        </Badge>
      ) : null}
    </>
  );
}

/** Hotel state for the PNR. A reserved hotel (`hotelReservationUrn` set) →
 *  "Hotel held" while the reversible offer awaits the passenger's acceptance,
 *  "Hotel booked" once booking CONFIRMED it (`hotelReservationStatus`; legacy rows
 *  without it read as booked); else `requiresHotel===true` → an overnight is
 *  required; `false` (re-accommodated same-day / next morning) → "No hotel". Hidden
 *  until a flight is assigned (the requirement is derived from the assigned schedule). */
function HotelBadge({ group }: { group: DisruptionPnrGroupRow }) {
  const { t } = useTranslation('pnr');
  if (group.hotelReservationUrn) {
    if (group.hotelReservationStatus === 'HELD') {
      return (
        <Badge
          variant="secondary"
          size="sm"
          title={t('hotelBadge.heldTitle')}
        >
          {t('hotelBadge.held')}
        </Badge>
      );
    }
    return (
      <Badge variant="success" size="sm" title={t('hotelBadge.bookedTitle')}>
        {t('hotelBadge.booked')}
      </Badge>
    );
  }
  // The accommodation KIND decided by rebooking (resolution-engine §4) drives the chip;
  // legacy rows without it fall back to the requiresHotel boolean.
  const kind = group.accommodationKind;
  if (kind === 'HOTEL' || (kind == null && group.requiresHotel === true)) {
    return (
      <Badge variant="warning" size="sm" title={t('hotelBadge.neededTitle')}>
        {t('hotelBadge.needed')}
      </Badge>
    );
  }
  if (kind === 'MEAL_VOUCHER') {
    const amt = group.mealVoucher?.amountUsd;
    const suffix = amt != null ? ` · $${amt}` : '';
    // #808: consumed at the counter (or cleared by the issuer) — the final state.
    if (group.mealVoucher?.redeemedAt) {
      const issuer = group.mealVoucher.redemptionSource === 'ISSUER';
      // #813: an operator redemption says who marked it and when.
      const stamp = issuer ? undefined : stampTitle({ by: group.mealVoucher.redeemedBy, at: group.mealVoucher.redeemedAt }, t);
      return (
        <Badge
          variant="success"
          size="sm"
          title={issuer ? t('hotelBadge.voucherRedeemedIssuerTitle') : `${t('hotelBadge.voucherRedeemedTitle')} · ${stamp}`}
          data-testid="roster-voucher-redeemed"
        >
          {`${t('hotelBadge.voucherRedeemed')}${suffix}`}
        </Badge>
      );
    }
    // Once wallet confirms the voucher card, the chip flips to the issued (success) state.
    if (group.mealVoucher?.issued) {
      return (
        <Badge variant="success" size="sm" title={t('hotelBadge.voucherIssuedTitle')}>
          {`${t('hotelBadge.voucherIssued')}${suffix}`}
        </Badge>
      );
    }
    return (
      <Badge variant="info" size="sm" title={t('hotelBadge.mealVoucherTitle')}>
        {`${t('hotelBadge.mealVoucher')}${suffix}`}
      </Badge>
    );
  }
  if (kind === 'NONE' || (kind == null && group.requiresHotel === false && group.assignedFlight)) {
    return (
      <Badge variant="muted" size="sm" title={t('hotelBadge.noneTitle')}>
        {t('hotelBadge.none')}
      </Badge>
    );
  }
  return null;
}

/** Below-the-badge explanation for a terminal no-options group: when the rebooking
 *  status is NO_OPTIONS / NOT_APPLICABLE and the group carries a coded `noOptionsReason`,
 *  show the human copy as a muted subline. For POLICY_DISABLED (the primary case) append
 *  a "View policy" link to the governing policy (the row carries `governingPolicyUrn`
 *  from POL-12). Renders nothing when the reason is absent (today's generic fallback). */
function NoOptionsReason({ group }: { group: DisruptionPnrGroupRow }) {
  const { t } = useTranslation('pnr');
  const terminal =
    group.rebookingStatus === 'NO_OPTIONS' || group.rebookingStatus === 'NOT_APPLICABLE';
  if (!terminal || !group.noOptionsReason) return null;
  const copy = noOptionsReasonLabel(group.noOptionsReason, t);
  if (!copy) return null;
  const isPolicyDisabled = group.noOptionsReason === 'POLICY_DISABLED';
  return (
    <p
      className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground"
      data-testid="roster-no-options-reason"
      title={copy}
    >
      <Scale className="h-3 w-3 shrink-0" aria-hidden />
      <span>{copy}</span>
      {isPolicyDisabled && group.governingPolicyUrn ? (
        <Link
          to={`/policies/${encodeURIComponent(group.governingPolicyUrn)}`}
          className="text-primary hover:underline"
          data-testid="roster-no-options-policy-link"
        >
          {t('policy.view')}
        </Link>
      ) : null}
    </p>
  );
}

/** Whether the row has any triage chip to show (connection / needs / outside-window),
 *  so the second chip row is only rendered when it carries something. */
function hasTriage(t: Translate, group: DisruptionPnrGroupRow): boolean {
  return (
    !!group.connection ||
    !!(group.needs && group.needs.length) ||
    !!recoveryDelay(t, group.departureAt, group.assignedFlight?.departureAt)?.warn
  );
}

/** At-a-glance triage chips on the roster row (rebooking-engine consolidation):
 *  connection (→ final destination), assistance-needs count, and the
 *  recovery-window guard (⚠ when the re-accommodation lands outside the window).
 *  The full picture lives in the PNR detail (one click on the row). */
function TriageChips({ group }: { group: DisruptionPnrGroupRow }) {
  const { t } = useTranslation('pnr');
  const rd = recoveryDelay(t, group.departureAt, group.assignedFlight?.departureAt);
  const needs = group.needs ?? [];
  return (
    <>
      {group.connection ? (
        <Badge
          variant="info"
          size="sm"
          title={`${t('roster.connectionTitle', {
            destination: group.connection.finalDestination,
          })}${
            group.connection.via && group.connection.via.length
              ? t('roster.viaSuffix', { via: group.connection.via.join(', ') })
              : ''
          }`}
          data-testid="roster-chip-connection"
        >
          <PlaneTakeoff className="mr-1 h-3 w-3" aria-hidden />→{group.connection.finalDestination}
        </Badge>
      ) : null}
      {needs.length ? (
        <Badge
          variant="warning"
          size="sm"
          title={t('roster.needsTitle', { list: needs.map((c) => needLabel(t, c)).join(', ') })}
          data-testid="roster-chip-needs"
        >
          <Users className="mr-1 h-3 w-3" aria-hidden />
          {needs.length}
        </Badge>
      ) : null}
      {rd?.warn ? (
        <Badge
          variant="error"
          size="sm"
          title={t('roster.windowTitle')}
          data-testid="roster-chip-window"
        >
          ⚠ {rd.label}
        </Badge>
      ) : null}
    </>
  );
}

/** Disruption-window guard (rebooking-engine S3): the recovery delay between the
 *  original and re-accommodated departure, flagged `warn` when it pushes more than
 *  a day out (the passenger lands outside the disruption's same-day recovery
 *  window — escalate / confirm hotel). Null when either departure is unknown or
 *  the re-accommodation is not later than the original. */
function recoveryDelay(
  t: Translate,
  originalIso?: string,
  newIso?: string,
): { label: string; warn: boolean } | null {
  if (!originalIso || !newIso) return null;
  const ms = new Date(newIso).getTime() - new Date(originalIso).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const hours = ms / 3_600_000;
  const warn = hours >= 24;
  let label: string;
  if (hours < 1) label = `+${Math.round(ms / 60_000)}m`;
  else if (hours < 24) label = `+${Math.round(hours)}h`;
  else {
    const days = Math.round(hours / 24);
    label = t('roster.recoveryDays', { count: days });
  }
  return { label, warn };
}

/** An opaque, mask-by-default PAX id for the row subline (U1). Derived from the
 *  lead passenger's system URN (or the sub-case URN as a fallback) — its short
 *  trailing segment. NEVER the raw PNR locator (`pnrLabel`, shown separately) nor
 *  any PII. Returns null when no identifier is present. */
function paxOpaqueId(group: DisruptionPnrGroupRow): string | null {
  const urn = group.passengers?.[0]?.paxUrn ?? group.subCaseUrn;
  if (!urn) return null;
  const tail = urn.split(':').pop() || urn;
  const short = tail.length > 8 ? tail.slice(-8) : tail;
  return `#${short}`;
}

/** Translation KEY per assistance-need code (SSR) — keys here, copy in the `pnr`
 *  bundles under `need.*`. An unknown code falls back to the raw code. */
const NEED_KEYS: Record<string, string> = {
  WCHR: 'need.wchr',
  WCHS: 'need.wchs',
  WCHC: 'need.wchc',
  MAAS: 'need.maas',
  UMNR: 'need.umnr',
  BLND: 'need.blnd',
  DEAF: 'need.deaf',
  PETC: 'need.petc',
  INFT: 'need.inft',
};
function needLabel(t: Translate, code: string): string {
  const key = NEED_KEYS[code];
  return key ? t(key) : code;
}

/** The re-accommodated flight under the original — "→ LA456 GRU→SCL 10:40".
 *  Renders nothing until a flight is assigned (status SELECTED). */
function AssignedFlight({
  assigned,
}: {
  assigned?: DisruptionPnrGroupRow['assignedFlight'];
}) {
  if (!assigned || (!assigned.number && !assigned.destination)) return null;
  const route =
    assigned.origin && assigned.destination
      ? `${assigned.origin}→${assigned.destination}`
      : (assigned.destination ?? '');
  const time = assigned.departureAt
    ? formatFlightTime(assigned.departureAt, assigned.origin)
    : '';
  return (
    <div
      className="mt-0.5 flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400"
      data-testid="roster-assigned-flight"
    >
      <span aria-hidden>→</span>
      <span>{[assigned.number, route, time].filter(Boolean).join(' ')}</span>
    </div>
  );
}
